import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { SyncProvider } from './SyncProvider.js';
import { oauthProxyApi } from '@/api/index.js';

// ponytail: drive.file limits to files this app created; spreadsheets.readonly for Sheet import
const SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/spreadsheets.readonly',
].join(' ');
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

const USER_CANCELLED = new Set(['Popup closed', 'Popup blocked']);

// Token persistence: Capacitor Preferences (native) or localStorage (web)
const TOKEN_KEY = 'gdrive_token';
const EXPIRY_KEY = 'gdrive_token_exp';
const REFRESH_KEY = 'gdrive_refresh_token';

const store = {
  get: k =>
    Capacitor.isNativePlatform()
      ? Preferences.get({ key: k }).then(r => r.value)
      : Promise.resolve(localStorage.getItem(k)),
  remove: k =>
    Capacitor.isNativePlatform()
      ? Preferences.remove({ key: k })
      : (localStorage.removeItem(k), Promise.resolve()),
  set: (k, v) =>
    Capacitor.isNativePlatform()
      ? Preferences.set({ key: k, value: v })
      : (localStorage.setItem(k, v), Promise.resolve()),
};

export class GoogleDriveProvider extends SyncProvider {
  protected _token: string | null;
  protected _expiresAt: number;
  protected _refresh: string | null;
  protected _nativeReady: boolean;

  constructor() {
    super();
    // In-memory token cache; seeded from storage on web, restored async on native
    this._token = Capacitor.isNativePlatform() ? null : (localStorage.getItem(TOKEN_KEY) ?? null);
    this._expiresAt = Capacitor.isNativePlatform()
      ? 0
      : Number.parseInt(localStorage.getItem(EXPIRY_KEY) ?? '0', 10);
    this._refresh = Capacitor.isNativePlatform()
      ? null
      : (localStorage.getItem(REFRESH_KEY) ?? null);
    this._nativeReady = false;

    if (Capacitor.isNativePlatform()) {
      Promise.all([store.get(TOKEN_KEY), store.get(EXPIRY_KEY), store.get(REFRESH_KEY)]) // NOSONAR ponytail: fire-and-forget; top-level await blocks native plugin init
        .then(([t, e, r]) => {
          if (t) {
            this._token = t;
            this._expiresAt = Number.parseInt(e ?? '0', 10);
          }
          if (r) this._refresh = r;
        });
    }
  }

  static isUserCancelledOAuth(e) {
    return USER_CANCELLED.has(e?.message);
  }

  // ── Token management ───────────────────────────────────────────────────────

  _setToken(token, expiresInSeconds = 3599, refreshToken = null) {
    this._token = token;
    this._expiresAt = Date.now() + (expiresInSeconds - 60) * 1000;
    store.set(TOKEN_KEY, token);
    store.set(EXPIRY_KEY, String(this._expiresAt));
    if (refreshToken) {
      this._refresh = refreshToken;
      store.set(REFRESH_KEY, refreshToken);
    }
  }

  clearToken() {
    this._token = null;
    this._expiresAt = 0;
    this._refresh = null;
    store.remove(TOKEN_KEY);
    store.remove(EXPIRY_KEY);
    store.remove(REFRESH_KEY);
  }

  async _silentRefresh() {
    if (!this._refresh) throw new Error('token_expired');
    let data;
    try {
      data = await oauthProxyApi.refresh(this._refresh);
    } catch {
      throw new Error('token_expired');
    }
    if (!data.access_token) throw new Error('token_expired');
    this._setToken(data.access_token, data.expires_in ?? 3599);
    return data.access_token;
  }

  async _getAccessToken() {
    if (this._token && Date.now() < this._expiresAt - REFRESH_BUFFER_MS) return this._token;
    if (this._refresh && !Capacitor.isNativePlatform()) return this._silentRefresh();
    throw new Error('token_expired');
  }

  // ── OAuth flows ────────────────────────────────────────────────────────────

  async _webOAuth(): Promise<{ expiresIn: number; refreshToken: string | null; token: string }> {
    const redirectUri = globalThis.location.origin + '/oauth/callback';
    const { challenge, verifier } = await _generatePKCE();
    const params = new URLSearchParams({
      access_type: 'offline',
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      prompt: 'consent',
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: SCOPES,
    });
    const popup = globalThis.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
      'google-oauth',
      'width=520,height=620,left=200,top=100',
    );
    if (!popup) throw new Error('Popup blocked');
    return new Promise((resolve, reject) => {
      function onMessage(e) {
        if (e.origin !== globalThis.location.origin || e.data?.type !== 'google-oauth-pkce') return;
        cleanup();
        if (e.data.code) {
          _exchangeCode(e.data.code, verifier, redirectUri)
            .then(d =>
              resolve({
                expiresIn: d.expires_in ?? 3599,
                refreshToken: d.refresh_token ?? null,
                token: d.access_token,
              }),
            )
            .catch(reject);
        } else {
          reject(new Error(e.data.error || 'OAuth failed'));
        }
      }
      const poll = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Popup closed'));
        }
      }, 500);
      function cleanup() {
        clearInterval(poll);
        globalThis.removeEventListener('message', onMessage);
      }
      globalThis.addEventListener('message', onMessage);
    });
  }

  async _nativeOAuth(): Promise<{ expiresIn: number; refreshToken?: string; token: string }> {
    if (!this._nativeReady) {
      await SocialLogin.initialize({
        google: { mode: 'online', webClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID },
      });
      this._nativeReady = true;
    }
    const res = (await SocialLogin.login({
      options: { scopes: [SCOPES] },
      provider: 'google',
    })) as any;
    if (res?.result?.accessToken?.token)
      return {
        expiresIn: res.result.accessToken.expiresIn ?? 3599,
        token: res.result.accessToken.token,
      };
    throw new Error('Native OAuth failed');
  }

  // ── SyncProvider contract ──────────────────────────────────────────────────

  override async signIn() {
    const result = await (Capacitor.isNativePlatform() ? this._nativeOAuth() : this._webOAuth());
    this._setToken(result.token, result.expiresIn, result.refreshToken ?? null);
  }

  override async signOut() {
    this.clearToken();
    if (Capacitor.isNativePlatform() && this._nativeReady) {
      await SocialLogin.logout({ provider: 'google' }).catch(() => {});
    }
  }

  override async uploadData(name, data) {
    const token = await this._getAccessToken();
    const folderId = await this._ensureFolder(token);
    return this._uploadFile(
      token,
      folderId,
      name,
      new Blob([JSON.stringify(data)], { type: 'application/json' }),
      'application/json',
    );
  }

  override async downloadData(name) {
    const token = await this._getAccessToken();
    const folderId = await this._ensureFolder(token);
    return this._downloadFile(token, folderId, name);
  }

  // ── Google-specific extensions ─────────────────────────────────────────────

  async uploadBlob(name, blob, mimeType) {
    const token = await this._getAccessToken();
    const folderId = await this._ensureFolder(token);
    return this._uploadFile(token, folderId, name, blob, mimeType);
  }

  async fetchSheetValues(sheetId) {
    const token = await this._getAccessToken();
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(sheetId)}/values/A1:Z`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Sheets API ${sheetId} → ${res.status}`);
    const { values } = await res.json();
    return values ?? [];
  }

  // ── Drive API internals ────────────────────────────────────────────────────

  async _driveRequest(
    token: string,
    path: string,
    opts: RequestInit & { headers?: Record<string, string> } = {},
  ): Promise<any> {
    const res = await fetch(`https://www.googleapis.com${path}`, {
      ...opts,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...opts.headers,
      },
    });
    if (!res.ok) throw new Error(`Drive ${opts.method ?? 'GET'} ${path} → ${res.status}`);
    return res.json();
  }

  async _ensureFolder(token) {
    const q = `name='${SyncProvider.FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    const { files } = await this._driveRequest(
      token,
      `/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`,
    );
    if (files.length) return files[0].id;
    const folder = await this._driveRequest(token, '/drive/v3/files', {
      body: JSON.stringify({
        mimeType: 'application/vnd.google-apps.folder',
        name: SyncProvider.FOLDER_NAME,
      }),
      method: 'POST',
    });
    return folder.id;
  }

  async _uploadFile(token, folderId, name, blob, mimeType) {
    const q = `name='${name}' and '${folderId}' in parents and trashed=false`;
    const { files } = await this._driveRequest(
      token,
      `/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`,
    );
    const form = new FormData();
    form.append(
      'metadata',
      new Blob(
        [JSON.stringify({ name, ...(files.length ? {} : { parents: [folderId] }), mimeType })],
        { type: 'application/json' },
      ),
    );
    form.append('file', blob);
    const url = files.length
      ? `https://www.googleapis.com/upload/drive/v3/files/${files[0].id}?uploadType=multipart`
      : 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
    const res = await fetch(url, {
      body: form,
      headers: { Authorization: `Bearer ${token}` },
      method: files.length ? 'PATCH' : 'POST',
    });
    if (!res.ok) throw new Error(`uploadFile ${name} → ${res.status}`);
  }

  async _downloadFile(token, folderId, name) {
    const q = `name='${name}' and '${folderId}' in parents and trashed=false`;
    const { files } = await this._driveRequest(
      token,
      `/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`,
    );
    if (!files.length) return null;
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${files[0].id}?alt=media`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok ? res.json() : null;
  }
}

async function _generatePKCE() {
  const rand = crypto.getRandomValues(new Uint8Array(48));
  const verifier = btoa(String.fromCodePoint(...rand))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCodePoint(...new Uint8Array(digest)))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
  return { challenge, verifier };
}

async function _exchangeCode(code, verifier, redirectUri) {
  const data = await oauthProxyApi.exchangeCode(code, verifier, redirectUri);
  if (!data.access_token) throw new Error(data.error ?? 'token_exchange_failed');
  return data;
}

// ── Module singleton ─────────────────────────────────────────────────────────
// One instance so the in-memory access-token cache is shared by every caller.
// Exported as the instance rather than a factory: destructuring methods off a
// class loses `this`, so callers use googleDrive.method(...) directly.
export const googleDrive = new GoogleDriveProvider();

export const driveSignIn = () => googleDrive.signIn();
export const driveSignOut = () => googleDrive.signOut();
export const isUserCancelledOAuth = (e: unknown) => GoogleDriveProvider.isUserCancelledOAuth(e);
export const resolveManifest = (local, remote) => SyncProvider.resolveManifest(local, remote);
